/**
 * Pure helpers behind the `JsonTool` page. Kept free of React /
 * lucide imports so the SPA's vitest setup (`environment: 'node'`)
 * can exercise them without a DOM.
 */

export type JsonOk = { ok: true; output: string };
export type JsonErr = { ok: false; error: string; line?: number; column?: number };
export type JsonResult = JsonOk | JsonErr;

export type ParseOk = { ok: true; value: unknown };
export type ParseErr = { ok: false; error: string; line?: number; column?: number };
export type ParseResult = ParseOk | ParseErr;

/**
 * Parse + return the value (separate from format/minify which return
 * a stringified output). The tree-view renderer needs the live value;
 * having a single `parseJson` keeps error reporting consistent
 * between the text and tree affordances.
 */
export function parseJson(raw: string): ParseResult {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: EMPTY_INPUT_MSG };
  }
  try {
    const value = JSON.parse(trimmed) as unknown;
    return { ok: true, value };
  } catch (err) {
    return { ok: false, ...explainJsonParseError(err, trimmed) };
  }
}

const EMPTY_INPUT_MSG = '输入为空';

/**
 * Format a JSON string with `indent` spaces. Returns a structured
 * result instead of throwing so the UI can render the raw error
 * line/column without a try/catch on every keystroke.
 *
 * Empty / whitespace-only input is treated as a hard error so the
 * preview pane stays stable instead of flashing to "" between edits.
 */
export function formatJson(raw: string, indent = 2): JsonResult {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: EMPTY_INPUT_MSG };
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return { ok: true, output: JSON.stringify(parsed, null, indent) };
  } catch (err) {
    return { ok: false, ...explainJsonParseError(err, trimmed) };
  }
}

/**
 * Produce the most compact representation `JSON.stringify` allows —
 * no whitespace at all. Same error shape as {@link formatJson} so the
 * UI can branch on `result.ok` once.
 */
export function minifyJson(raw: string): JsonResult {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: EMPTY_INPUT_MSG };
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return { ok: true, output: JSON.stringify(parsed) };
  } catch (err) {
    return { ok: false, ...explainJsonParseError(err, trimmed) };
  }
}

/**
 * V8 / SpiderMonkey throw `SyntaxError` whose `message` looks like:
 *   "Unexpected token } in JSON at position 13"
 * We surface the position back as 1-based line/column so the UI can
 * jump the cursor to the right spot. Engines that don't include the
 * position (Bun / very old runtimes) just get the raw message back.
 */
function explainJsonParseError(err: unknown, raw: string): { error: string; line?: number; column?: number } {
  const msg = err instanceof Error ? err.message : String(err);
  const m = /at position (\d+)/.exec(msg);
  if (!m) return { error: msg };
  const pos = Number(m[1]);
  const { line, column } = positionToLineCol(raw, pos);
  return { error: msg, line, column };
}

function positionToLineCol(raw: string, pos: number): { line: number; column: number } {
  let line = 1;
  let column = 1;
  const upto = Math.min(pos, raw.length);
  for (let i = 0; i < upto; i++) {
    if (raw.charCodeAt(i) === 10) {
      line++;
      column = 1;
    } else {
      column++;
    }
  }
  return { line, column };
}
